import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Button, Chip, CircularProgress, Alert, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EventIcon from '@mui/icons-material/Event';
import PlaceIcon from '@mui/icons-material/Place';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1';
import CloseIcon from '@mui/icons-material/Close';
import { PsfColors } from '../theme';
import EventShareDialog from '../components/EventShareDialog';
import {
  ConsumerEvent, ConsumerEventInvite,
  fetchConsumerEvents, createConsumerEvent, updateConsumerEvent, deleteConsumerEvent,
  fetchEventInvites, addEventInvites, deleteEventInvite,
  sendEventInvite,
} from '../services/api';

interface ConsumerEventsPageProps {
  c: PsfColors;
}

interface EventFormState {
  name: string;
  description: string;
  venue: string;
  startAt: string;
  endAt: string;
  capacity: string;
}

const EMPTY_FORM: EventFormState = { name: '', description: '', venue: '', startAt: '', endAt: '', capacity: '' };

interface InviteRow { name: string; email: string; mobile: string; }

type InvitationWindowState = 'open' | 'started' | 'expired';

const fmtDateTime = (d: string | null) =>
  d ? new Date(d).toLocaleString('en-SG', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

export default function ConsumerEventsPage({ c }: ConsumerEventsPageProps) {
  const C = c;
  const [events, setEvents] = useState<ConsumerEvent[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Create/edit dialog
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<EventFormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Invite dialog
  const [inviteEvent, setInviteEvent] = useState<ConsumerEvent | null>(null);
  const [invites, setInvites] = useState<ConsumerEventInvite[]>([]);
  const [invitesLoading, setInvitesLoading] = useState(false);
  const [newRows, setNewRows] = useState<InviteRow[]>([{ name: '', email: '', mobile: '' }]);
  const [inviteSaving, setInviteSaving] = useState(false);
  const [sendingInviteId, setSendingInviteId] = useState<number | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteEmailNotice, setInviteEmailNotice] = useState<string | null>(null);
  const [inviteWindowBlocked, setInviteWindowBlocked] = useState(false);
  const [share, setShare] = useState<{ url: string; name: string } | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Keep the invite controls in sync as an event enters (or leaves) the
  // invitation window without requiring the page to be refreshed.
  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 15_000);
    return () => window.clearInterval(timer);
  }, []);

  const getInvitationWindowState = useCallback((event: ConsumerEvent | null): InvitationWindowState => {
    if (!event) return 'started';
    if (!event.endAt) return 'expired';
    if (now >= new Date(event.endAt).getTime() + 30 * 60 * 1000) return 'expired';
    if (now >= new Date(event.startAt).getTime()) return 'started';
    return 'open';
  }, [now]);

  // A backend rejection can race the client clock at the boundary. Once the
  // event is visibly in the open window, allow the controls to recover on the
  // next timer tick without requiring the dialog to be reopened.
  useEffect(() => {
    if (inviteEvent && inviteWindowBlocked && getInvitationWindowState(inviteEvent) === 'open') {
      setInviteWindowBlocked(false);
    }
  }, [getInvitationWindowState, inviteEvent, inviteWindowBlocked]);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchConsumerEvents();
      setEvents(res.data);
      setIsAdmin(res.isAdmin);
    } catch (e: any) {
      setError(e.message || 'Failed to load events');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setFormOpen(true);
  };

  const openEdit = (ev: ConsumerEvent) => {
    setEditingId(ev.id);
    setForm({
      name: ev.name,
      description: ev.description || '',
      venue: ev.venue || '',
      startAt: ev.startAt ? toLocalInput(ev.startAt) : '',
      endAt: ev.endAt ? toLocalInput(ev.endAt) : '',
      capacity: ev.capacity != null ? String(ev.capacity) : '',
    });
    setFormError(null);
    setFormOpen(true);
  };

  const submitForm = async () => {
    if (!form.name.trim()) { setFormError('Event name is required'); return; }
    if (!form.startAt) { setFormError('Start date/time is required'); return; }
    if (!form.endAt) { setFormError('End date/time is required'); return; }
    const start = new Date(form.startAt);
    const end = new Date(form.endAt);
    if (Number.isNaN(start.getTime())) { setFormError('Start date/time is invalid'); return; }
    if (Number.isNaN(end.getTime())) { setFormError('End date/time is invalid'); return; }
    // New events cannot be created in the past. Existing historical events
    // remain editable, but their end must still follow their start.
    const currentMinute = Math.floor(Date.now() / 60_000) * 60_000;
    if (editingId == null && start.getTime() < currentMinute) {
      setFormError('Start date/time cannot be in the past'); return;
    }
    if (end.getTime() <= start.getTime()) {
      setFormError('End date/time must be after the start date/time'); return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const body = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        venue: form.venue.trim() || undefined,
        startAt: start.toISOString(),
        endAt: end.toISOString(),
        capacity: form.capacity ? Number(form.capacity) : null,
      };
      if (editingId != null) await updateConsumerEvent(editingId, body);
      else await createConsumerEvent(body);
      setFormOpen(false);
      loadEvents();
    } catch (e: any) {
      setFormError(e.message || 'Failed to save event');
    } finally {
      setSaving(false);
    }
  };

  const removeEvent = async (ev: ConsumerEvent) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete event "${ev.name}"? Its invites will no longer be visible.`)) return;
    try {
      await deleteConsumerEvent(ev.id);
      loadEvents();
    } catch (e: any) {
      setError(e.message || 'Failed to delete event');
    }
  };

  const openInvites = async (ev: ConsumerEvent) => {
    setInviteEvent(ev);
    setNewRows([{ name: '', email: '', mobile: '' }]);
    setInviteError(null);
    setInviteEmailNotice(null);
    setInviteWindowBlocked(false);
    setInvitesLoading(true);
    try {
      const res = await fetchEventInvites(ev.id);
      setInvites(res.data);
    } catch (e: any) {
      setInviteError(e.message || 'Failed to load invites');
    } finally {
      setInvitesLoading(false);
    }
  };

  const submitInvites = async () => {
    if (!inviteEvent) return;
    const invitationState = getInvitationWindowState(inviteEvent);
    if (invitationState === 'started') {
      setInviteError('Consumers cannot be added after the event has started.');
      return;
    }
    const filled = newRows.filter((r) => r.name.trim() || r.email.trim() || r.mobile.trim());
    if (filled.length === 0) { setInviteError('Add at least one consumer'); return; }
    for (const r of filled) {
      if (!r.name.trim()) { setInviteError('Every consumer needs a name'); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email.trim())) { setInviteError(`Invalid email for ${r.name}`); return; }
      if (!/^\+?[0-9 -]{8,20}$/.test(r.mobile.trim())) { setInviteError(`Invalid mobile for ${r.name}`); return; }
    }
    setInviteSaving(true);
    setInviteError(null);
    setInviteEmailNotice(null);
    try {
      const result = await addEventInvites(inviteEvent.id, filled.map((r) => ({
        name: r.name.trim(), email: r.email.trim(), mobile: r.mobile.trim(),
      })));
      const emailResults = result.emailResults || [];
      const skipped = emailResults.filter((row) => row.status === 'skipped').map((row) => row.email);
      const failed = emailResults.filter((row) => row.status === 'failed').map((row) => row.email);
      if (skipped.length || failed.length) {
        const details = [
          skipped.length ? `Email skipped in development: ${skipped.join(', ')}` : '',
          failed.length ? `Email failed: ${failed.join(', ')}` : '',
        ].filter(Boolean).join(' · ');
        setInviteEmailNotice(details);
      }
      const res = await fetchEventInvites(inviteEvent.id);
      setInvites(res.data);
      setNewRows([{ name: '', email: '', mobile: '' }]);
      loadEvents();
    } catch (e: any) {
      if (e?.code === 'INVITES_NOT_OPEN') {
        setInviteWindowBlocked(true);
      }
      setInviteError(e.message || 'Failed to save invites');
    } finally {
      setInviteSaving(false);
    }
  };

  const sendInvite = async (invite: ConsumerEventInvite) => {
    if (!inviteEvent) return;
    const state = getInvitationWindowState(inviteEvent);
    if (state === 'expired') {
      setInviteError('This event access window has ended.');
      return;
    }
    setSendingInviteId(invite.id);
    setInviteError(null);
    try {
      const result = await sendEventInvite(inviteEvent.id, invite.id);
      setShare({ url: result.fullUrl, name: result.name || invite.name });
      setInvites((prev) => prev.map((row) => row.id === invite.id
        ? {
          ...row,
          invitationSentAt: result.invitationSentAt,
          personalizedLinkSentAt: result.personalizedLinkSentAt,
          hasPersonalizedLink: true,
        }
        : row));
    } catch (e: any) {
      if (e?.code === 'INVITES_NOT_OPEN') setInviteWindowBlocked(true);
      setInviteError(e.message || 'Failed to send invitation');
    } finally {
      setSendingInviteId(null);
    }
  };

  const removeInvite = async (inviteId: number) => {
    if (!inviteEvent) return;
    try {
      await deleteEventInvite(inviteEvent.id, inviteId);
      setInvites((prev) => prev.filter((i) => i.id !== inviteId));
      loadEvents();
    } catch (e: any) {
      setInviteError(e.message || 'Failed to remove invite');
    }
  };

  const fieldSx = {
    '& input, & textarea': { color: C.text, fontSize: '0.8rem' },
    '& label': { color: C.textMuted, fontSize: '0.8rem' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: C.border },
  } as const;

  return (
    <Box sx={{ flex: 1, p: 2.5, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 800, color: C.text, fontSize: '1.05rem' }}>
            Consumer Events
          </Typography>
          <Typography variant="caption" sx={{ color: C.textMuted }}>
            Invite consumers to upcoming events — they’ll receive an email invitation
          </Typography>
        </Box>
        {(
          <Button
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{
              bgcolor: C.orange, color: '#fff', textTransform: 'none', fontWeight: 700,
              px: 2, borderRadius: 1.5, '&:hover': { bgcolor: C.orangeLight },
            }}
          >
            Create Event
          </Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, py: 0.5, fontSize: '0.8rem' }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={30} sx={{ color: C.orange }} />
        </Box>
      ) : events.length === 0 ? (
        <Paper elevation={0} sx={{
          p: 5, textAlign: 'center', bgcolor: C.surfaceCard,
          border: `1px dashed ${C.border}`, borderRadius: 2,
        }}>
          <EventIcon sx={{ fontSize: 42, color: C.textMuted, mb: 1 }} />
          <Typography sx={{ color: C.textMuted, fontSize: '0.9rem' }}>
            No events yet{isAdmin ? ' — create the first one' : '. Check back once an admin has created one.'}
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(310px, 1fr))', gap: 2 }}>
          {events.map((ev) => {
            const upcoming = new Date(ev.startAt).getTime() >= now;
            return (
              <Paper key={ev.id} elevation={0} sx={{
                p: 2, bgcolor: C.surfaceCard, border: `1px solid ${C.border}`, borderRadius: 2,
                display: 'flex', flexDirection: 'column', gap: 1,
                transition: 'all 0.15s', '&:hover': { borderColor: C.orange },
              }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: 1.5, flexShrink: 0,
                    bgcolor: upcoming ? `${C.orange}1e` : C.surfaceElevated,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <EventIcon sx={{ color: upcoming ? C.orange : C.textMuted, fontSize: 22 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, color: C.text, fontSize: '0.9rem', lineHeight: 1.3 }}>
                      {ev.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: C.textMuted }}>
                      {fmtDateTime(ev.startAt)}{ev.endAt ? ` – ${new Date(ev.endAt).toLocaleTimeString('en-SG', { timeStyle: 'short' })}` : ''}
                    </Typography>
                  </Box>
                  <Chip
                    size="small"
                    label={upcoming ? 'Upcoming' : 'Past'}
                    sx={{
                      height: 18, fontSize: '0.6rem', fontWeight: 700,
                      bgcolor: upcoming ? `${C.success}22` : C.surfaceElevated,
                      color: upcoming ? C.success : C.textMuted,
                    }}
                  />
                </Box>
                {ev.description && (
                  <Typography sx={{ fontSize: '0.74rem', color: C.textMuted, lineHeight: 1.45 }}>
                    {ev.description}
                  </Typography>
                )}
                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {ev.venue && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                      <PlaceIcon sx={{ fontSize: 13, color: C.textMuted }} />
                      <Typography sx={{ fontSize: '0.7rem', color: C.textMuted }}>{ev.venue}</Typography>
                    </Box>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                    <PeopleAltIcon sx={{ fontSize: 13, color: C.textMuted }} />
                    <Typography sx={{ fontSize: '0.7rem', color: C.textMuted }}>
                      {ev.inviteCount} invited{ev.capacity ? ` / ${ev.capacity} capacity` : ''} · {ev.myInviteCount} by you
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 0.5 }}>
                  <Button
                    size="small"
                    startIcon={<PersonAddAlt1Icon sx={{ fontSize: 15 }} />}
                    onClick={() => openInvites(ev)}
                    sx={{
                      flex: 1, bgcolor: `${C.orange}18`, color: C.orange, textTransform: 'none',
                      fontWeight: 700, fontSize: '0.72rem', borderRadius: 1.5,
                      '&:hover': { bgcolor: `${C.orange}30` },
                    }}
                  >
                    Consumer List
                  </Button>
                  {ev.canManage && (
                    <>
                      <Tooltip title="Edit event">
                        <IconButton size="small" onClick={() => openEdit(ev)} sx={{ color: C.textMuted, border: `1px solid ${C.border}`, borderRadius: 1.5 }}>
                          <EditIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete event">
                        <IconButton size="small" onClick={() => removeEvent(ev)} sx={{ color: C.danger, border: `1px solid ${C.border}`, borderRadius: 1.5 }}>
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* ── Create / edit dialog ── */}
      <Dialog open={formOpen} onClose={() => setFormOpen(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { bgcolor: C.surfaceCard, border: `1px solid ${C.border}`, borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 800, color: C.text, fontSize: '1rem' }}>
          {editingId != null ? 'Edit Event' : 'Create Event'}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.75, pt: '10px !important' }}>
          {formError && <Alert severity="error" sx={{ py: 0.5, fontSize: '0.8rem' }}>{formError}</Alert>}
          <TextField label="Event name *" size="small" value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} sx={fieldSx} />
          <TextField label="Description" size="small" multiline minRows={2} value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} sx={fieldSx} />
          <TextField label="Venue" size="small" value={form.venue}
            onChange={(e) => setForm((f) => ({ ...f, venue: e.target.value }))} sx={fieldSx} />
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <TextField label="Starts *" type="datetime-local" size="small" value={form.startAt}
              inputProps={{
                min: editingId == null ? toLocalInput(new Date().toISOString()) : undefined,
                max: form.endAt || undefined,
              }}
              onChange={(e) => setForm((f) => {
                const nextStart = e.target.value;
                return {
                  ...f,
                  startAt: nextStart,
                  endAt: f.endAt && f.endAt <= nextStart ? '' : f.endAt,
                };
              })}
              InputLabelProps={{ shrink: true }} sx={{ ...fieldSx, flex: 1 }} />
            <TextField label="Ends" type="datetime-local" size="small" value={form.endAt}
              inputProps={{
                min: form.startAt || (editingId == null ? toLocalInput(new Date().toISOString()) : undefined),
              }}
              onChange={(e) => setForm((f) => ({ ...f, endAt: e.target.value }))}
              InputLabelProps={{ shrink: true }} sx={{ ...fieldSx, flex: 1 }} />
          </Box>
          <TextField label="Capacity (optional)" type="number" size="small" value={form.capacity}
            onChange={(e) => setForm((f) => ({ ...f, capacity: e.target.value }))} sx={{ ...fieldSx, width: 180 }} />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setFormOpen(false)} sx={{ color: C.textMuted, textTransform: 'none' }}>Cancel</Button>
          <Button
            onClick={submitForm}
            disabled={saving}
            sx={{ bgcolor: C.orange, color: '#fff', textTransform: 'none', fontWeight: 700, px: 2.5, '&:hover': { bgcolor: C.orangeLight } }}
          >
            {saving ? 'Saving…' : editingId != null ? 'Save Changes' : 'Create Event'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Invite dialog ── */}
      <Dialog open={inviteEvent != null} onClose={() => setInviteEvent(null)} maxWidth="md" fullWidth
        PaperProps={{ sx: { bgcolor: C.surfaceCard, border: `1px solid ${C.border}`, borderRadius: 2 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PersonAddAlt1Icon sx={{ color: C.orange }} />
          <Box sx={{ flex: 1 }}>
            <Typography sx={{ fontWeight: 800, color: C.text, fontSize: '0.95rem' }}>
              Consumer List — {inviteEvent?.name}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: C.textMuted }}>
              {inviteEvent ? fmtDateTime(inviteEvent.startAt) : ''}{inviteEvent?.venue ? ` · ${inviteEvent.venue}` : ''}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setInviteEvent(null)} sx={{ color: C.textMuted }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {inviteError && <Alert severity="error" sx={{ mb: 1.5, py: 0.5, fontSize: '0.8rem' }}>{inviteError}</Alert>}
          {inviteEmailNotice && <Alert severity="warning" sx={{ mb: 1.5, py: 0.5, fontSize: '0.8rem' }}>{inviteEmailNotice}</Alert>}

          {inviteEvent && (() => {
            const invitationState = getInvitationWindowState(inviteEvent);
            const canAddInvites = invitationState !== 'started';
            const timingMessage = invitationState === 'started'
              ? 'The event has started. New consumers can no longer be added; existing consumers may still receive their links.'
              : invitationState === 'expired' ? 'This event access window has ended.' : null;
            return (
              <>
                {timingMessage && (
                  <Alert severity="info" sx={{ mb: 1.5, py: 0.5, fontSize: '0.8rem' }}>
                    {timingMessage}
                  </Alert>
                )}

                  <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: C.text, mb: 0.75 }}>
                  Add consumer
                </Typography>
                {newRows.map((row, i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                    <TextField disabled={!canAddInvites} placeholder="Name *" size="small" value={row.name}
                      onChange={(e) => setNewRows((rows) => rows.map((r, j) => (j === i ? { ...r, name: e.target.value } : r)))}
                      sx={{ ...fieldSx, flex: 1.2 }} />
                    <TextField disabled={!canAddInvites} placeholder="Email *" size="small" value={row.email}
                      onChange={(e) => setNewRows((rows) => rows.map((r, j) => (j === i ? { ...r, email: e.target.value } : r)))}
                      sx={{ ...fieldSx, flex: 1.4 }} />
                    <TextField disabled={!canAddInvites} placeholder="Mobile *" size="small" value={row.mobile}
                      onChange={(e) => setNewRows((rows) => rows.map((r, j) => (j === i ? { ...r, mobile: e.target.value } : r)))}
                      sx={{ ...fieldSx, flex: 1 }} />
                    <IconButton
                      size="small"
                      disabled={!canAddInvites || newRows.length === 1}
                      onClick={() => setNewRows((rows) => rows.filter((_, j) => j !== i))}
                      sx={{ color: C.textMuted }}
                    >
                      <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Box>
                ))}
                <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                  <Button
                    size="small"
                    disabled={!canAddInvites}
                    startIcon={<AddIcon sx={{ fontSize: 14 }} />}
                    onClick={() => setNewRows((rows) => [...rows, { name: '', email: '', mobile: '' }])}
                    sx={{ color: C.orange, textTransform: 'none', fontSize: '0.72rem' }}
                  >
                    Add row
                  </Button>
                  <Box sx={{ flex: 1 }} />
                  <Button
                    size="small"
                    disabled={!canAddInvites || inviteSaving}
                    onClick={submitInvites}
                    sx={{
                      bgcolor: C.orange, color: '#fff', textTransform: 'none', fontWeight: 700,
                      px: 2, borderRadius: 1.5, '&:hover': { bgcolor: C.orangeLight },
                    }}
                  >
                    {inviteSaving ? 'Saving…' : 'Add Consumers'}
                  </Button>
                </Box>
              </>
            );
          })()}

          <Typography sx={{ fontSize: '0.74rem', fontWeight: 700, color: C.text, mb: 0.75 }}>
            {isAdmin ? 'All invites' : 'Your invites'} ({invites.length})
          </Typography>
          {invitesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={22} sx={{ color: C.orange }} />
            </Box>
          ) : invites.length === 0 ? (
            <Typography sx={{ fontSize: '0.75rem', color: C.textMuted, py: 1 }}>
              No consumers invited yet.
            </Typography>
          ) : (
            <Table size="small" sx={{
              '& th': { color: C.textMuted, fontSize: '0.68rem', fontWeight: 700, borderColor: C.border, py: 0.5 },
              '& td': { color: C.text, fontSize: '0.74rem', borderColor: C.border, py: 0.5 },
            }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Mobile</TableCell>
                  {isAdmin && <TableCell>Invited By</TableCell>}
                  <TableCell>Added At</TableCell>
                  <TableCell>Personalized PSF Link</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {invites.map((inv) => (
                  <TableRow key={inv.id} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{inv.name}</TableCell>
                    <TableCell>{inv.email}</TableCell>
                    <TableCell>{inv.mobile}</TableCell>
                    {isAdmin && <TableCell>{inv.invitedBy}</TableCell>}
                    <TableCell>{fmtDateTime(inv.createdAt)}</TableCell>
                    <TableCell>
                      {inv.personalizedLinkSentAt && (
                        <Chip
                          size="small"
                          label={`Link sent · ${fmtDateTime(inv.personalizedLinkSentAt)}`}
                          sx={{
                            mr: 0.75,
                            height: 22,
                            color: '#237a3b',
                            bgcolor: 'rgba(46, 125, 50, 0.12)',
                            fontSize: '0.62rem',
                            fontWeight: 700,
                          }}
                        />
                      )}
                      {getInvitationWindowState(inviteEvent) !== 'expired' ? (
                        <Button size="small" disabled={sendingInviteId === inv.id} onClick={() => sendInvite(inv)} sx={{ color: C.orange, textTransform: 'none', fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                          {sendingInviteId === inv.id ? 'Opening…' : 'Send Invite'}
                        </Button>
                      ) : (
                        <Typography sx={{ color: C.textMuted, fontSize: '0.65rem', whiteSpace: 'nowrap' }}>Event ended</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" onClick={() => removeInvite(inv.id)} sx={{ color: C.danger, p: 0.25 }}>
                        <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>
      <EventShareDialog open={Boolean(share)} url={share?.url ?? null} consumerName={share?.name ?? ''} eventName={inviteEvent?.name ?? ''} c={C} onClose={() => setShare(null)} />
    </Box>
  );
}

/** ISO string → value usable by <input type="datetime-local"> (local time). */
function toLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
